
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/sonner';
import { getCourierByPhone } from '@/utils/supabase/courier';
import { addNotification } from '@/services/notificationService';

export interface PaymentInfo {
  id: string;
  rentalId: string;
  rentalHumanNumber?: string | null;
  courierId: string;
  courierName: string;
  amount: number;
  date: string;
  status: 'paid' | 'pending' | 'error' | 'completed' | 'auth';
  method: 'card' | 'cash' | 'account' | 'sbp';
  human_number?: string | null;
  receiptUrl?: string;
  subscriptionId?: string | null;
}

export function useCourierPayments(forcedCourierId?: string) {
  const { user } = useAuth();
  const [payments, setPayments] = useState<PaymentInfo[]>([]);
  const [totalPaid, setTotalPaid] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Ключ для localStorage, чтобы отслеживать последнее время запроса платежей
  const LAST_PAYMENTS_FETCH_KEY = 'lastPaymentsFetch';

  // Функция для проверки, нужно ли обновлять платежи
  const shouldFetchPayments = (): boolean => {
    try {
      const lastFetchTimeStr = localStorage.getItem(LAST_PAYMENTS_FETCH_KEY);
      if (!lastFetchTimeStr) return true;
      
      const lastFetchTime = parseInt(lastFetchTimeStr, 10);
      const now = Date.now();
      
      // Обновляем не чаще, чем раз в 5 минут
      return (now - lastFetchTime) > 5 * 60 * 1000;
    } catch (e) {
      console.error('Ошибка при проверке времени последнего обновления платежей:', e);
      return true;
    }
  };
  
  // Функция для установки времени последнего запроса
  const updateLastFetchTime = (): void => {
    try {
      localStorage.setItem(LAST_PAYMENTS_FETCH_KEY, Date.now().toString());
    } catch (e) {
      console.error('Ошибка при сохранении времени последнего обновления платежей:', e);
    }
  };

  useEffect(() => {
    const fetchPayments = async () => {
      // Проверяем, нужно ли загружать платежи заново
      if (!shouldFetchPayments() && payments.length > 0) {
        console.log('Пропускаем обновление платежей, прошло менее 5 минут с последнего запроса');
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        let courierId: string | null = forcedCourierId || null;
        
        // Если ID не передан, пытаемся найти по телефону пользователя
        if (!courierId && user?.phone) {
          const { data: courierData, error: courierError } = await getCourierByPhone(user.phone);
          
          if (courierError) {
            console.error('Ошибка при поиске курьера:', courierError);
          }
          
          if (courierData) {
            courierId = courierData.id;
            console.log('Найден ID курьера по телефону:', courierId);
          }
        }
        
        if (!courierId) {
          console.log('ID курьера не определен');
          setPayments([]);
          setTotalPaid(0);
          setLoading(false);
          return;
        }
        
        console.log('Запрашиваем платежи для курьера ID:', courierId);
        
        // Получаем платежи из базы данных
        const { data, error: paymentsError } = await supabase
          .from('payments')
          .select('*, rental:rentals(id, human_number)')
          .eq('courier_id', courierId)
          .order('payment_date', { ascending: false });
          
        if (paymentsError) {
          throw new Error(`Ошибка при загрузке платежей: ${paymentsError.message}`);
        }
        
        if (data) {
          // Преобразуем данные из базы в формат для компонента
          const formattedPayments: PaymentInfo[] = data.map(payment => ({
            id: payment.id,
            rentalId: payment.rental_id || '',
            rentalHumanNumber: payment.rental?.human_number || null,
            courierId: payment.courier_id,
            courierName: 'Курьер', // Используем значение по умолчанию
            amount: payment.amount,
            date: payment.payment_date,
            status: payment.status as any,
            method: payment.payment_method as any,
            human_number: payment.human_number,
            receiptUrl: payment.receipt_url,
            subscriptionId: payment.subscription_id
          }));
          
          setPayments(formattedPayments);
          
          // Вычисляем общую сумму только для успешных платежей (paid или completed)
          const paidTotal = formattedPayments
            .filter(p => p.status === 'paid' || p.status === 'completed')
            .reduce((sum, p) => sum + p.amount, 0);
          
          setTotalPaid(paidTotal);
          
          // Показываем уведомления для последних платежей (только при первой загрузке)
          const recentPayments = formattedPayments
            .filter(p => {
              const paymentTime = new Date(p.date).getTime();
              // Показываем уведомления только для платежей за последние 8 часов
              return (Date.now() - paymentTime) < 8 * 60 * 60 * 1000;
            });
            
          recentPayments.forEach(payment => {
            let notificationType: 'payment' | 'warning' | 'system' = 'payment';
            let notificationTitle = 'Информация о платеже';
            
            if (payment.status === 'pending') {
              notificationType = 'warning';
              notificationTitle = 'Ожидается оплата';
            } else if (payment.status === 'error') {
              notificationType = 'warning';
              notificationTitle = 'Ошибка платежа';
            }
            
            // Создаем уникальный ID, включающий статус платежа
            const notificationId = `payment-${payment.id}-${payment.status}`;
            
            // Добавляем уведомление через сервис
            addNotification({
              id: notificationId,
              title: notificationTitle,
              message: `Платеж #${payment.human_number || payment.id} на сумму ${payment.amount} руб.`,
              type: notificationType
            });
          });
          
          // Обновляем время последнего запроса
          updateLastFetchTime();
        } else {
          setPayments([]);
          setTotalPaid(0);
        }
      } catch (err: any) {
        console.error('Ошибка при загрузке данных о платежах:', err);
        setError(err.message || 'Не удалось загрузить данные о платежах');
        toast.error('Ошибка при загрузке платежей');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPayments();
  }, [user, forcedCourierId, payments.length]);

  return {
    payments,
    totalPaid,
    loading,
    error,
    lastPaymentAmount: payments.length > 0 ? payments[0].amount : null
  };
}
